import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import PageBreadcrumb from "@/components/feature/PageBreadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import { categories, works } from "@/mocks/comics";

export default function Works() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("cat") || "all";
  const [keyword, setKeyword] = useState("");

  const setCat = (key: string) => {
    if (key === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ cat: key });
    }
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return works.filter((w) => {
      const inCat = active === "all" || w.category === active;
      if (!inCat) return false;
      if (!kw) return true;
      return (
        w.title.toLowerCase().includes(kw) ||
        w.author.toLowerCase().includes(kw) ||
        w.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
  }, [active, keyword]);

  const breadcrumbJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "作品列表" },
        ],
      },
      {
        "@type": "CollectionPage",
        name: "漫蛙漫画网作品列表",
        inLanguage: "zh-CN",
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title="漫蛙漫画网作品列表 | 连载漫画大全与最新章节"
        description="漫蛙漫画网作品列表收录热血、奇幻、科幻、治愈等各类连载漫画，支持按题材分类筛选与关键词搜索，轻松找到你想追的最新漫画作品。"
        keywords="漫蛙漫画网作品列表,漫画分类,连载漫画,最新章节"
        jsonLd={breadcrumbJson}
      />

      <div className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 pb-8 pt-24 md:px-6 md:pt-28">
          <PageBreadcrumb items={[{ label: "首页", to: "/" }, { label: "作品列表" }]} />
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                作品列表
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                共收录 {works.length} 部作品 · 分类筛选 + 关键词搜索，直达最新章节
              </p>
            </div>
            <label className="flex w-full max-w-xs items-center gap-2 rounded-full bg-background-50 px-4 py-2.5 ring-1 ring-background-200 focus-within:ring-2 focus-within:ring-primary-400 sm:w-64">
              <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品 / 作者 / 标签"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </label>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCat(c.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === c.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <p className="mb-6 text-sm text-foreground-500">
            找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 部作品
          </p>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {filtered.map((w) => (
                <ComicCard key={w.id} work={w} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl bg-background-100 px-6 py-20 text-center ring-1 ring-background-200">
              <i className="ri-search-eye-line text-4xl text-foreground-300" aria-hidden="true" />
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到匹配的作品</p>
              <p className="mt-2 text-sm text-foreground-500">换个关键词，或切换其他题材分类试试</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}